import "./style.css";
import "./looper/looper.css";
import type { Patch } from "./engine/types";
import { clonePatch, coercePatch } from "./engine/defaultPatch";
import { LooperEngine } from "./looper/LooperEngine";
import { makeDefaultProject } from "./looper/presets";
import type { LooperProject } from "./looper/types";
import { CellsView } from "./looper/ui/cells";
import { ArrangementView } from "./looper/ui/arrangement";
import { buildRibbon } from "./looper/ui/ribbon";
import { buildKeyboard } from "./ui/keyboard";
import { Visualizer } from "./ui/visualizer";
import { buildControls, buildMorphRack } from "./ui/controls";
import type { ParamRef } from "./ui/controls";
import { ContextSlider } from "./ui/contextSlider";
import { buildToolbar } from "./ui/toolbar";
import { randomPatch, mutatePatch } from "./engine/randomPatch";
import { morphPatch } from "./engine/morphPatch";
import { loadPalette, savePalette, genId, colorFor } from "./shared/palette";
import type { Palette, PaletteSlot, PhraseSlot, PhraseEvent } from "./shared/palette";
import { PaletteView } from "./shared/paletteView";
import { seedKitIfFirstRun } from "./shared/kitPresets";
import { registerDropZone } from "./shared/dragDrop";
import { UndoStack } from "./shared/undo";
import { expandNotes, arpPlan, DEFAULT_HARMONY } from "./engine/harmony";
import type { HarmonySettings } from "./engine/harmony";
import { buildHarmonyBar } from "./ui/harmonyBar";

const STORAGE_KEY = "wap.project";

function isInteractive(): boolean {
  const el = document.activeElement;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLButtonElement
  );
}
const editorOpen = (): boolean => document.body.dataset.editor === "open";

function loadProject(): LooperProject {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (p && typeof p.bpm === "number" && Array.isArray(p.cells) && Array.isArray(p.palette) && p.palette.length) {
        return p as LooperProject;
      }
    }
  } catch {
    // ignore
  }
  return makeDefaultProject();
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const app = document.getElementById("app");
if (!app) throw new Error("#app not found");

app.innerHTML = `
  <header class="appbar">
    <canvas id="viz" class="viz-top"></canvas>
    <div class="appbar-overlay">
      <span class="brand">betaudio</span>
    </div>
  </header>
  <!-- Top bar: unified Record + Play, then the always-visible scrollable
       sample row (the clipboard, reachable in every orientation). -->
  <div class="top-bar" id="top-bar">
    <div class="top-controls">
      <button class="top-btn rec-main rec-notes" id="rec-main" type="button" aria-label="Record" title="Hold to record · double-tap to arm">
        <span class="rec-dot"></span>
        <span class="rec-ctx" id="rec-ctx"></span>
      </button>
      <button class="top-btn play-main play" id="play-main" type="button" aria-label="Play / Stop" title="Play / Stop (Space)">
        <span class="play-glyph">▶</span>
      </button>
    </div>
    <div class="step-tab" title="Your sound bank. Click an empty slot to save the current sound; drag a tile onto a box or the song.">
      <span class="step">02</span><span class="step-name">Bank</span>
    </div>
    <div class="palette-host" id="palette-host"></div>
  </div>
  <main class="studio">
    <section class="panel" id="sound-panel">
      <div class="panel-head">
        <span class="step">01</span>
        <h2>Design a sound</h2>
        <span class="panel-sub">drag any chip · roll the dice · morph two together</span>
        <div class="toolbar patch-tools" id="toolbar"></div>
      </div>
      <div class="sound-row">
        <div class="controls morph-rack" id="morph-rack"></div>
        <div class="controls" id="controls"></div>
      </div>
    </section>
    <section class="panel" id="loops-panel">
      <div class="panel-head">
        <span class="step">03</span>
        <h2>Layer into boxes</h2>
        <span class="panel-sub">drop bank tiles in · hold Record to play notes into the focused box</span>
        <div class="transport loops-bar" id="loopsbar"></div>
      </div>
      <div class="boxes" id="boxes"></div>
    </section>
    <section class="panel" id="arrangement-panel">
      <div class="panel-head">
        <span class="step">04</span>
        <h2>Arrange the song</h2>
        <span class="panel-sub">drag a box down here, or hit <b>+ Song</b> on it · drag = move · tap = preview</span>
        <div class="toolbar arr-tools">
          <button id="arr-play" class="tool" type="button" title="Play the arrangement once, left to right">▶ Play</button>
          <button id="arr-json" class="tool" type="button" title="Download the project JSON (engine + arrangement)">{ } JSON</button>
          <button id="arr-wav"  class="tool" type="button" title="Render the arrangement to a WAV file (respects FX)">⤓ WAV</button>
        </div>
      </div>
      <div id="arrangement"></div>
    </section>
  </main>
  <p class="hint" id="hint"></p>
  <button class="undo-chip" id="undo-chip" type="button" hidden>↶ Undo</button>
  <div class="input-dock">
    <div id="ribbon"></div>
    <div id="harmony-bar"></div>
    <div id="keyboard"></div>
  </div>
`;

const canvas = app.querySelector<HTMLCanvasElement>("#viz")!;
const loopsbarEl = app.querySelector<HTMLElement>("#loopsbar")!;
const toolbarEl = app.querySelector<HTMLElement>("#toolbar")!;
const controlsEl = app.querySelector<HTMLElement>("#controls")!;
const boxesEl = app.querySelector<HTMLElement>("#boxes")!;
const ribbonEl = app.querySelector<HTMLElement>("#ribbon")!;
const keyboardEl = app.querySelector<HTMLElement>("#keyboard")!;
const harmonyBarEl = app.querySelector<HTMLElement>("#harmony-bar")!;
const undoChip = app.querySelector<HTMLButtonElement>("#undo-chip")!;
const hintEl = app.querySelector<HTMLElement>("#hint")!;
const paletteHost = app.querySelector<HTMLElement>("#palette-host")!;
const morphRackEl = app.querySelector<HTMLElement>("#morph-rack")!;
const soundPanel = app.querySelector<HTMLElement>("#sound-panel")!;
const loopsPanel = app.querySelector<HTMLElement>("#loops-panel")!;

// Which area the user is working in, so Space does the contextual thing:
// preview the current sound while designing, play/stop the loop while looping.
let area: "sound" | "loop" = "sound";
soundPanel.addEventListener("pointerdown", () => { area = "sound"; }, true);
loopsPanel.addEventListener("pointerdown", () => { area = "loop"; }, true);

let project = loadProject();
const engine = new LooperEngine(project);
// Restore the FX preference (UI-only state — never persisted in the project JSON).
try {
  const stored = localStorage.getItem("wap.fx");
  if (stored === "0") engine.setFxEnabled(false);
} catch {
  // ignore
}
let lastMidi = 60;

// How a key press maps to notes (single / chord / overtones). A performance
// preference like the FX toggle, so it lives in localStorage — loading someone
// else's project must never silently change how your keyboard plays.
// Key is versioned: changing a *default* otherwise never reaches anyone who has
// already touched the controls, because their stored value wins. Bump the
// suffix when the defaults change and everyone picks them up once.
const HARMONY_KEY = "wap.harmony2";
const harmony: HarmonySettings = { ...DEFAULT_HARMONY };
try {
  const stored = localStorage.getItem(HARMONY_KEY);
  if (stored) Object.assign(harmony, JSON.parse(stored) as Partial<HarmonySettings>);
  localStorage.removeItem("wap.harmony"); // superseded
} catch {
  // ignore
}
function persistHarmony(): void {
  try {
    localStorage.setItem(HARMONY_KEY, JSON.stringify(harmony));
  } catch {
    // ignore
  }
}

const visualizer = new Visualizer(canvas, engine.analyser);
visualizer.start();

function persistProject(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(engine.currentProject));
  } catch {
    // ignore
  }
}

// ---- Clipboard palette (the sound bank) ----
// Declared before the sound section because the Morph slots are drop targets
// for its tiles.
const palette: Palette = loadPalette();
// One-time: drop a starter drum/metal kit into empty slots (never overwrites
// saved tiles; deleting a kit tile is permanent).
seedKitIfFirstRun(palette);

// ---- Undo ----
// Snapshot-based rather than command-based: project + palette are plain JSON,
// so cloning them can't drift out of sync the way hand-written inverse
// operations do. A wrong undo would be worse than none. Snapshots are taken
// *before* the destructive act, via each view's `beforeChange` hook.
const undoStack = new UndoStack(20, () => updateUndoChip());
function snapshotForUndo(label: string): void {
  const proj = JSON.parse(JSON.stringify(engine.currentProject)) as LooperProject;
  const pal = JSON.parse(JSON.stringify(palette)) as Palette;
  undoStack.push(label, () => {
    project = proj;
    engine.setProject(project);
    // The palette array is shared by reference with PaletteView — refill it in
    // place rather than rebinding, or the view keeps the old array.
    palette.length = 0;
    palette.push(...pal);
    savePalette(palette);
    cellsView.render();
    arrangement.reload();
    paletteView.render();
    rebuildControls();
    renderTransport();
    persistProject();
  });
}
function updateUndoChip(): void {
  const label = undoStack.topLabel;
  undoChip.hidden = !label;
  undoChip.title = label ? `Undo: ${label}` : "";
  undoChip.textContent = label ? `↶ Undo ${label}` : "↶ Undo";
}
undoChip.addEventListener("click", () => {
  undoStack.undo();
});

// ---- Sound design: edit the current sound (a project palette entry) ----
const contextSlider = new ContextSlider(document.body);

// The right slider shows Master by default; tapping a fader switches it to that
// parameter. So Master is the home/default for the one big slider.
const masterRef: ParamRef = {
  label: "Master",
  min: 0,
  max: 1,
  step: 0.01,
  scale: "lin",
  fmt: (v) => v.toFixed(2),
  get: () => engine.editPatch()?.masterGain ?? 0.8,
  set: (v) => {
    const p = engine.editPatch();
    if (!p) return;
    p.masterGain = v;
    engine.setMaster(v);
    persistProject();
  },
};

function onSoundChange(): void {
  const p = engine.editPatch();
  if (p) engine.setMaster(p.masterGain);
  persistProject();
}

// ---- Morph A↔B ----
// Blend two sounds into a third, real one: dragging the fader turns the edit
// patch into the *interpolated in-between sound* (a concrete portable Patch —
// play it, save it to a slot, export it). t=0 is pure A, t=1 pure B.
//
// Both ends are explicit slots you fill by clicking (= take the current sound)
// or by dropping a sample tile. Loading a crossfade tile still fills both at
// once. Previously the whole control only existed after that undiscoverable
// drag-tile-onto-tile handshake, which is why Victor could never find it.
// `open` is UI-only: collapsed to a single chip until you actually want it, so
// an unused Morph doesn't sit there looking permanently disabled.
const morph: { a: Patch | null; b: Patch | null; t: number; open: boolean } =
  { a: null, b: null, t: 0, open: false };
let morphRebuild: number | null = null;
const morphArmed = (): boolean => morph.a !== null && morph.b !== null;
const morphRef: ParamRef = {
  label: "Morph",
  min: 0,
  max: 1,
  step: 0.01,
  scale: "lin",
  fmt: (v) => `${Math.round(v * 100)}%`,
  get: () => morph.t,
  set: (v) => {
    morph.t = v;
    applyMorph();
  },
};
function applyMorph(): void {
  const p = engine.editPatch();
  if (!p || !morph.a || !morph.b) return;
  const m = morphPatch(morph.a, morph.b, morph.t);
  Object.assign(p, m);
  delete p.xfadeTo; // the morph IS the blend now — don't also crossfade voices
  engine.setMaster(p.masterGain);
  // Refresh the main rack (debounced) so its faders glide to the morphed
  // values. Only #controls — never the morph rack itself, so a live drag on
  // the Morph fader is never interrupted by its own rebuild.
  if (morphRebuild !== null) clearTimeout(morphRebuild);
  morphRebuild = window.setTimeout(() => {
    const p2 = engine.editPatch();
    if (p2) buildControls(controlsEl, p2, onSoundChange, (ref) => contextSlider.bind(ref));
    persistProject();
  }, 180);
}
// Put a sound into A or B. Called by click (current sound) and by tile drop.
function setMorphEnd(which: "a" | "b", patch: Patch): void {
  const clean = clonePatch(patch);
  delete clean.xfadeTo;
  morph[which] = clean;
  // Filling the second slot lands you at that end, so the first drag is a
  // move *away* from a sound you recognise rather than a jump into the middle.
  if (morphArmed()) morph.t = which === "b" ? 1 : 0;
  renderMorph();
  if (morphArmed()) applyMorph();
}

let morphZoneCleanup: Array<() => void> = [];
function renderMorph(): void {
  for (const off of morphZoneCleanup) off();
  morphZoneCleanup = [];
  const rack = buildMorphRack(
    morphRackEl,
    { a: morph.a?.name ?? null, b: morph.b?.name ?? null, t: morph.t, open: morph.open },
    morphRef,
    {
      onPick: (which) => {
        const cur = engine.editPatch();
        if (cur) setMorphEnd(which, cur);
      },
      onToggle: () => {
        morph.open = true;
        renderMorph();
      },
      // ✕ = empty both ends and collapse. The edit patch keeps whatever the
      // morph last produced — clearing the slots shouldn't yank your sound away.
      onClear: () => {
        morph.a = null;
        morph.b = null;
        morph.t = 0;
        morph.open = false;
        renderMorph();
        if (contextSlider.boundLabel() === morphRef.label) contextSlider.bind(masterRef);
      },
      onSelect: (r) => contextSlider.bind(r),
    },
  );
  for (const [which, elz] of [["a", rack.slotA], ["b", rack.slotB]] as const) {
    if (!elz) continue; // collapsed — no slots on screen
    morphZoneCleanup.push(
      registerDropZone({
        el: elz,
        overClass: "dragover",
        accepts: (d) => d.kind === "slot",
        onDrop: (d) => {
          const slot = palette[Number(d.id)];
          if (slot) setMorphEnd(which, slot.patch);
        },
      }),
    );
  }
}

function rebuildControls(): void {
  const p = engine.editPatch();
  if (p) buildControls(controlsEl, p, onSoundChange, (ref) => contextSlider.bind(ref));
  renderMorph();
}
function loadEditPatch(patch: Patch): void {
  engine.loadEditPatch(patch);
  // A crossfade tile fills both morph ends at once (A = the patch minus its
  // target). Otherwise the slots keep whatever the user put there.
  if (patch.xfadeTo) {
    const a = clonePatch(patch);
    delete a.xfadeTo;
    morph.a = a;
    morph.b = clonePatch(patch.xfadeTo);
    morph.t = 0;
    morph.open = true; // a crossfade tile is a morph pair — show it
  }
  rebuildControls();
  persistProject();
}

// Raw-JSON editor for the current edit patch. Power-user / debug surface;
// opens via the { } chip in the loops bar. Live edits apply on a 350 ms
// debounce; invalid JSON shows a red status without breaking the running sound.
function openJsonEditor(): void {
  const patch = engine.editPatch();
  if (!patch) return;
  if (document.querySelector(".json-backdrop")) return; // one at a time

  const backdrop = document.createElement("div");
  backdrop.className = "json-backdrop";
  const panel = document.createElement("div");
  panel.className = "json-panel";
  const head = document.createElement("div");
  head.className = "json-head";
  const title = document.createElement("span");
  title.textContent = "Patch JSON · live";
  const closeBtn = document.createElement("button");
  closeBtn.className = "json-close";
  closeBtn.textContent = "✕";
  head.append(title, closeBtn);

  const textarea = document.createElement("textarea");
  textarea.className = "json-textarea";
  textarea.spellcheck = false;
  textarea.autocomplete = "off";
  textarea.value = JSON.stringify(patch, null, 2);

  const status = document.createElement("span");
  status.className = "json-status";
  status.textContent = "tip: edits apply automatically · Esc to close";

  panel.append(head, textarea, status);
  backdrop.append(panel);
  document.body.append(backdrop);

  let applyTimer: number | null = null;
  const tryApply = (): void => {
    try {
      const parsed = JSON.parse(textarea.value);
      const coerced = coercePatch(parsed);
      loadEditPatch(coerced);
      status.textContent = "✓ applied";
      status.className = "json-status ok";
    } catch (e) {
      const msg = (e as Error).message || "invalid JSON";
      status.textContent = `✕ ${msg.slice(0, 80)}`;
      status.className = "json-status err";
    }
  };
  textarea.addEventListener("input", () => {
    if (applyTimer !== null) clearTimeout(applyTimer);
    applyTimer = window.setTimeout(tryApply, 350);
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  }
  function close(): void {
    if (applyTimer !== null) clearTimeout(applyTimer);
    backdrop.remove();
    window.removeEventListener("keydown", onKey, true);
  }
  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("pointerdown", (e) => {
    if (e.target === backdrop) close();
  });
  window.addEventListener("keydown", onKey, true);

  textarea.focus();
}

rebuildControls();
contextSlider.bind(masterRef); // Master is the slider's home until a fader is tapped
onSoundChange();

buildToolbar(toolbarEl, {
  getPatch: () => engine.editPatch() ?? clonePatch(makeDefaultProject().palette[0].patch),
  onLoad: loadEditPatch,
});
// 🎲 Random + Mutate (sfxr-style sound discovery) — roll a whole new sound, or
// nudge every knob of the current one a little. Both play immediately.
const randBtn = document.createElement("button");
randBtn.textContent = "🎲 Random";
randBtn.title = "Roll a brand-new random sound";
randBtn.addEventListener("click", () => {
  void engine.resume();
  const p = randomPatch();
  loadEditPatch(p);
  engine.previewPatch(p, lastMidi);
});
const mutBtn = document.createElement("button");
mutBtn.textContent = "≈ Mutate";
mutBtn.title = "Nudge every parameter of the current sound a little";
mutBtn.addEventListener("click", () => {
  const cur = engine.editPatch();
  if (!cur) return;
  void engine.resume();
  const p = mutatePatch(cur);
  loadEditPatch(p);
  engine.previewPatch(p, lastMidi);
});
toolbarEl.prepend(randBtn, mutBtn);
// Help "?" link — sits under Reset in the patch-tools column.
const helpLink = document.createElement("a");
helpLink.className = "help-link";
helpLink.href = "/help.html";
helpLink.target = "_blank";
helpLink.rel = "noopener";
helpLink.title = "Open the betaudio manual";
helpLink.textContent = "? Help";
toolbarEl.append(helpLink);

function previewSlot(slot: PaletteSlot): void {
  void engine.resume();
  if (slot.kind === "patch") {
    engine.previewPatch(slot.patch, slot.lastMidi);
    return;
  }
  const start = engine.ctx.currentTime + 0.05;
  for (const ev of slot.events) engine.scheduleNote(slot.patch, ev.midi, start + ev.time, ev.dur);
}
const paletteView = new PaletteView(paletteHost, {
  palette,
  captureCurrent: () => ({ patch: engine.editPatch() ?? clonePatch(makeDefaultProject().palette[0].patch), lastMidi }),
  // Selecting a tile loads its sound into the synth section (so it's ready to use/edit).
  onPreview: (slot) => {
    area = "sound";
    // Selecting a sample switches the Record context away from any focused box.
    engine.focusCell(null);
    loadEditPatch(clonePatch(slot.patch));
    previewSlot(slot);
  },
  onLoad: (loaded) => loadEditPatch(loaded),
  beforeChange: snapshotForUndo,
});

// ---- Phrase recorder: capture played notes into a clipboard phrase ----
const recBtn = app.querySelector<HTMLButtonElement>("#rec-main")!;
recBtn.title = "Hold to record · double-tap to arm. Records the focused box, else a new sample.";

let phraseRecording = false;
let recStart = -1;
const recEvents: PhraseEvent[] = [];
const pending = new Map<number, number>();
let phraseCount = 0;

function startPhrase(): void {
  if (phraseRecording) return;
  void engine.resume();
  phraseRecording = true;
  recStart = -1;
  recEvents.length = 0;
  pending.clear();
  recBtn.classList.add("recording");
}
function stopPhrase(): void {
  if (!phraseRecording) return;
  phraseRecording = false;
  recBtn.classList.remove("recording");
  const now = engine.ctx.currentTime;
  for (const [midi, t0] of pending) recEvents.push({ midi, time: t0 - recStart, dur: now - t0 });
  pending.clear();
  if (recEvents.length === 0) return;
  const p = engine.editPatch();
  if (!p) return;
  phraseCount += 1;
  const slot: PhraseSlot = {
    id: genId(),
    kind: "phrase",
    name: `Phrase ${phraseCount}`,
    color: colorFor(p),
    patch: clonePatch(p),
    events: recEvents.slice(),
    length: recStart >= 0 ? now - recStart : 0,
  };
  paletteView.addSlot(slot);
}
// ---- Unified Record button (top bar) ----
// Context-aware: a focused box records into that box; otherwise it captures a
// phrase into the clipboard (a new sample). One button, two gestures:
//   • hold  = record only while held (momentary)
//   • double-tap = arm & latch: waits for input, records until tapped again
let recContext: "box" | "phrase" | null = null;
function startContextRecording(): void {
  void engine.resume();
  if (engine.recordCellId) {
    recContext = "box";
    engine.startRecording();
  } else {
    recContext = "phrase";
    startPhrase();
  }
  recBtn.classList.add("recording");
  updatePlay();
}
function stopContextRecording(): void {
  if (recContext === "box") engine.stopRecording();
  else if (recContext === "phrase") stopPhrase();
  recContext = null;
  recBtn.classList.remove("recording", "armed");
  updatePlay();
}

let recLatched = false;
let recHoldTimer: number | null = null;
let recHoldActive = false;
let recLastTapUp = 0;
const REC_HOLD_MS = 160;
const REC_DTAP_MS = 320;

recBtn.addEventListener("pointerdown", (e) => {
  e.preventDefault();
  // While latched (armed/recording), a press stops & commits.
  if (recLatched) {
    recLatched = false;
    stopContextRecording();
    return;
  }
  const now = performance.now();
  // Double-tap → arm latch mode (records until tapped again).
  if (now - recLastTapUp < REC_DTAP_MS) {
    recLastTapUp = 0;
    recLatched = true;
    recBtn.classList.add("armed");
    startContextRecording();
    return;
  }
  // Otherwise: if held past the threshold, momentary record while held.
  recHoldTimer = window.setTimeout(() => {
    recHoldTimer = null;
    recHoldActive = true;
    startContextRecording();
  }, REC_HOLD_MS);
});
const recRelease = (): void => {
  if (recHoldTimer !== null) {
    // Released before the hold fired → it was a tap (maybe 1st of a double-tap).
    clearTimeout(recHoldTimer);
    recHoldTimer = null;
    recLastTapUp = performance.now();
    return;
  }
  if (recHoldActive) {
    recHoldActive = false;
    stopContextRecording();
  }
};
recBtn.addEventListener("pointerup", recRelease);
recBtn.addEventListener("pointercancel", recRelease);

// ---- Shared note input (keyboard + ribbon) ----
// One key press can sound several notes (chord / harmonic series). The mapping
// is a pure function in `engine/harmony.ts`; here we just remember which notes
// a given key spawned so releasing it releases all of them. Strum staggers the
// note-ons in real time, which means it also lands in any recording.
const heldNotes = new Map<number, number[]>();
const strumTimers = new Map<number, number[]>();
// Arpeggio: `harmony.ts` stays pure and only *describes* the pattern (order +
// step/gate in seconds for a given BPM); the clock lives here.
const arpTimers = new Map<number, number>();

function startNote(midi: number): void {
  if (phraseRecording) {
    if (recStart < 0) recStart = engine.ctx.currentTime;
    pending.set(midi, engine.ctx.currentTime);
  }
  engine.noteOn(midi);
}
function stopNote(midi: number): void {
  engine.noteOff(midi);
  const t0 = pending.get(midi);
  if (phraseRecording && t0 !== undefined) {
    pending.delete(midi);
    recEvents.push({ midi, time: t0 - recStart, dur: engine.ctx.currentTime - t0 });
  }
}

function noteOnInput(midi: number): void {
  area = "sound";
  lastMidi = midi;
  ribbon.setExternal(midi); // mirror the played note onto the ribbon
  // The ribbon is a continuous-pitch instrument — expanding it into a chord
  // would fight the glide, so harmony applies to discrete key presses only.
  if (arpTimers.has(midi)) return; // OS key-repeat would otherwise stack intervals
  const notes = expandNotes(midi, harmony);
  heldNotes.set(midi, notes);

  // Arpeggio replaces strum entirely — they're alternatives, not layers.
  const plan = arpPlan(notes, harmony, project.bpm);
  if (plan) {
    let step = 0;
    const tick = (): void => {
      const n = plan.order[step++ % plan.order.length];
      startNote(n);
      window.setTimeout(() => stopNote(n), plan.gateSec * 1000);
    };
    tick(); // step 0 is the press itself, so there's no delay before the first note
    arpTimers.set(midi, window.setInterval(tick, plan.stepSec * 1000));
    return;
  }

  const timers: number[] = [];
  notes.forEach((n, i) => {
    const delay = i * harmony.strumMs;
    if (delay <= 0) startNote(n);
    else timers.push(window.setTimeout(() => startNote(n), delay));
  });
  if (timers.length) strumTimers.set(midi, timers);
}
function noteOffInput(midi: number): void {
  ribbon.setExternal(null); // leave a ghost of the released note
  const arp = arpTimers.get(midi);
  if (arp !== undefined) {
    clearInterval(arp);
    arpTimers.delete(midi);
  }
  // Cancel any strum notes that haven't sounded yet — releasing early should
  // not fire the rest of the chord after your finger is gone.
  for (const t of strumTimers.get(midi) ?? []) clearTimeout(t);
  strumTimers.delete(midi);
  for (const n of heldNotes.get(midi) ?? [midi]) stopNote(n);
  heldNotes.delete(midi);
}
// Changing mode mid-hold would orphan the notes the old setting spawned.
function releaseAllHeld(): void {
  for (const midi of [...heldNotes.keys()]) noteOffInput(midi);
}

// ---- Loops ----
const cellsView = new CellsView(boxesEl, engine, {
  // Song chips derive their letter/length/notes from the boxes, so any box edit
  // has to re-render them or they go stale (wrong letter after a delete, etc.).
  onChange: () => {
    persistProject();
    arrangement.refresh();
  },
  // "+ Song" on a box card — the no-drag path onto the arrangement, for when
  // dragging the ⠿ grip across the page is awkward (phone, short screen).
  onAddToArrangement: (cellId) => arrangement.appendCell(cellId),
  // Drop a palette tile onto a box: a phrase fills the box with its notes;
  // a single sound re-points the box's notes at that sound.
  onDropSlot: (cellId, idx) => {
    const slot = palette[idx];
    if (!slot) return;
    if (slot.kind === "phrase") engine.loadPhraseIntoCell(cellId, slot.patch, slot.events, slot.length);
    else engine.setCellSound(cellId, slot.patch);
    persistProject();
    arrangement.refresh();
  },
  // In the maximised editor: drop a palette tile onto a note (or empty) to give
  // it a different sound, so one box can mix several sounds. Snapshotted = robust.
  resolveDropSound: (idx) => {
    const slot = palette[idx];
    if (!slot) return null;
    const id = engine.registerSound(slot.patch);
    persistProject();
    return id;
  },
});
engine.onCellChanged = persistProject;

// ---- Arrangement — step 04. Drop boxes + bank tiles onto a wide, pannable
//      canvas: left-to-right is time, 1 beat = 32 px. ----
const arrangementEl = app.querySelector<HTMLElement>("#arrangement")!;
const arrPlayBtn = app.querySelector<HTMLButtonElement>("#arr-play")!;
const arrangement = new ArrangementView(arrangementEl, {
  engine,
  palette,
  onChange: persistProject,
  // Playback ends on its own at the last chip, so the button label has to be
  // driven by the view rather than by the click that started it.
  onPlayStateChange: (playing) => {
    arrPlayBtn.textContent = playing ? "■ Stop" : "▶ Play";
    arrPlayBtn.classList.toggle("on", playing);
  },
});
arrPlayBtn.addEventListener("click", () => arrangement.play());
const safeProjectName = (): string => (project.name || "betaudio").replace(/[^a-z0-9_-]+/gi, "_");
app.querySelector<HTMLButtonElement>("#arr-json")!.addEventListener("click", () => {
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
  downloadBlob(blob, `${safeProjectName()}.betaudio.json`);
});
const arrWavBtn = app.querySelector<HTMLButtonElement>("#arr-wav")!;
arrWavBtn.addEventListener("click", async () => {
  const original = arrWavBtn.textContent;
  arrWavBtn.disabled = true;
  arrWavBtn.textContent = "Rendering…";
  try {
    const blob = await arrangement.exportWav();
    if (!blob) {
      alert("Drop some chips on the arrangement first.");
      return;
    }
    downloadBlob(blob, `${safeProjectName()}.arrangement.wav`);
  } finally {
    arrWavBtn.disabled = false;
    arrWavBtn.textContent = original;
  }
});

// ---- Transport: Play lives in the top bar next to Record ----
const playBtn = app.querySelector<HTMLButtonElement>("#play-main")!;
const playGlyph = playBtn.querySelector<HTMLElement>(".play-glyph")!;
const recCtxEl = app.querySelector<HTMLElement>("#rec-ctx")!;
playBtn.title = "Play / Stop (Space)";
let wasPlaying = false;
function updatePlay(): void {
  playGlyph.textContent = engine.playing ? "■" : "▶";
  playBtn.classList.toggle("playing", engine.playing);
  // The box timelines now idle instead of redrawing every frame forever, so
  // something has to restart their playhead loop when the transport starts.
  // Doing it on the transition (rather than in the click handler) covers every
  // route into playback — button, Space, and record auto-starting it.
  if (engine.playing && !wasPlaying) {
    cellsView.refresh();
    visualizer.wake();
  }
  wasPlaying = engine.playing;
  // Record-context badge: the focused box's letter (A, B, …) or nothing when
  // Record would capture a new sample instead.
  const cells = engine.currentProject.cells;
  const i = engine.recordCellId ? cells.findIndex((c) => c.id === engine.recordCellId) : -1;
  recCtxEl.textContent = i >= 0 ? String.fromCharCode(65 + (i % 26)) : "";
}
function transportToggle(): void {
  engine.toggle();
  updatePlay();
}
playBtn.addEventListener("click", transportToggle);
updatePlay();

function renderTransport(): void {
  loopsbarEl.innerHTML = "";

  const bpmLabel = document.createElement("label");
  bpmLabel.textContent = "BPM";
  const bpm = document.createElement("input");
  bpm.type = "number";
  bpm.min = "40";
  bpm.max = "240";
  bpm.value = String(project.bpm);
  bpm.addEventListener("change", () => {
    project.bpm = Math.min(240, Math.max(40, Number(bpm.value) || project.bpm));
    bpm.value = String(project.bpm);
    persistProject();
  });
  bpmLabel.append(bpm);

  // FX toggle — bypass compressor + voice scaling for an A/B with raw quality.
  // State persists in localStorage; engine starts with the stored value.
  const fxChip = document.createElement("button");
  fxChip.className = "io fx-chip";
  fxChip.title = "Engine FX (compressor + voice scaling). Off = raw oscillator sum.";
  const updateFxChip = (): void => {
    fxChip.textContent = engine.fxEnabled ? "FX ●" : "FX ○";
    fxChip.classList.toggle("on", engine.fxEnabled);
  };
  fxChip.addEventListener("click", () => {
    engine.setFxEnabled(!engine.fxEnabled);
    try { localStorage.setItem("wap.fx", engine.fxEnabled ? "1" : "0"); } catch { /* ignore */ }
    updateFxChip();
  });
  updateFxChip();

  // { } chip — opens a live JSON editor for the current edit patch.
  const jsonChip = document.createElement("button");
  jsonChip.className = "io json-chip";
  jsonChip.textContent = "{ }";
  jsonChip.title = "Edit the current sound as raw JSON";
  jsonChip.addEventListener("click", openJsonEditor);

  const saveBtn = document.createElement("button");
  saveBtn.className = "io";
  saveBtn.textContent = "Save";
  saveBtn.title = "Save project";
  saveBtn.addEventListener("click", () => {
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
    downloadBlob(blob, `${(project.name || "betaudio").replace(/[^a-z0-9_-]+/gi, "_")}.betaudio.json`);
  });

  const loadLabel = document.createElement("label");
  loadLabel.className = "file io";
  loadLabel.textContent = "Load";
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = "application/json,.json";
  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    try {
      const p = JSON.parse(await file.text());
      if (p && typeof p.bpm === "number" && Array.isArray(p.cells) && Array.isArray(p.palette) && p.palette.length) {
        project = p as LooperProject;
        engine.setProject(project);
        cellsView.render();
        // Must come before persistProject(), or the stale in-memory chips get
        // written back over the arrangement we just loaded.
        arrangement.reload();
        rebuildControls();
        renderTransport();
        persistProject();
      } else {
        alert("That doesn't look like a betaudio project.");
      }
    } catch {
      alert("Could not read that file.");
    }
    fileInput.value = "";
  });
  loadLabel.append(fileInput);

  // Add a box of a chosen meter (3/4, 4/4 or free rhythm).
  const addGroup = document.createElement("div");
  addGroup.className = "add-meters";
  for (const meter of ["4/4", "3/4", "free"] as const) {
    const b = document.createElement("button");
    b.className = "io add-meter";
    b.textContent = `+ ${meter}`;
    b.title = `Add a ${meter} box`;
    b.addEventListener("click", () => {
      cellsView.addCell(meter);
      persistProject();
    });
    addGroup.append(b);
  }

  loopsbarEl.append(bpmLabel, fxChip, jsonChip, saveBtn, loadLabel, addGroup);
}
renderTransport();

// ---- Bottom input dock ----
// Two switches, one per input area: the ribbon has Free/Notes (continuous
// pitch), the keyboard has Note/Chord/Harm.
buildHarmonyBar(harmonyBarEl, {
  settings: harmony,
  onChange: () => {
    releaseAllHeld(); // never orphan notes spawned under the old setting
    persistHarmony();
    // The ribbon's Scale mode follows the same key + scale, so its chip and
    // readout have to re-read them the moment the picker changes.
    ribbon.refresh();
    fitDock();
  },
  onSelect: (ref) => contextSlider.bind(ref),
});

const keyboard = buildKeyboard(keyboardEl, {
  startMidi: 48,
  octaves: 2,
  onNoteOn: noteOnInput,
  onNoteOff: noteOffInput,
});
// The ribbon mirrors its pitch on the keyboard: highlight the nearest key and
// follow the octave so it stays visible (ghost remains after release).
const ribbon = buildRibbon(ribbonEl, {
  lowHz: 1,
  highHz: 22000,
  onDown: (midi) => {
    area = "sound";
    lastMidi = midi;
    engine.ribbonDown(midi);
    keyboard.setExternal(midi);
  },
  onMove: (midi) => {
    engine.ribbonMove(midi);
    keyboard.setExternal(midi);
  },
  onUp: () => {
    engine.ribbonUp();
    keyboard.setExternal(null);
  },
  // Scale mode snaps slides to the keyboard's key + scale, so a glide lands in
  // key rather than merely in tune. Read live — the picker can change anytime.
  getHarmony: () => harmony,
});

// ---- Keyboard shortcuts ----
function previewCurrentSound(): void {
  const p = engine.editPatch();
  if (p) engine.previewPatch(p, lastMidi);
}
window.addEventListener("keydown", (e) => {
  // Cmd/Ctrl+Z anywhere except a text field.
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !isInteractive()) {
    e.preventDefault();
    undoStack.undo();
    return;
  }
  if (isInteractive()) return;
  if (e.code === "Space") {
    e.preventDefault();
    // In the maximised editor, Space plays/stops the loops so you can audition
    // while editing. Otherwise: playing → stop; Loops area → play; Sound area →
    // replay the current sound.
    if (editorOpen() || engine.playing || area === "loop") transportToggle();
    else previewCurrentSound();
  }
});
// Bottom-row keys (thumb-reachable while playing notes on the upper rows):
//   Hold B = record into the rec-primed box loop.
//   Hold N = capture played notes into a clipboard phrase.
// Two separate single-purpose keys (replaces the old dual-function R).
window.addEventListener("keydown", (e) => {
  if (e.repeat || isInteractive() || editorOpen()) return;
  const k = e.key.toLowerCase();
  if (k === "b" && engine.recordCellId) {
    e.preventDefault();
    engine.startRecording();
    updatePlay();
  } else if (k === "n") {
    e.preventDefault();
    startPhrase();
  }
});
window.addEventListener("keyup", (e) => {
  const k = e.key.toLowerCase();
  if (k === "b") engine.stopRecording();
  else if (k === "n") stopPhrase();
});

// Keep the play button synced (holding ● Rec / R can auto-start playback).
setInterval(updatePlay, 200);

hintEl.innerHTML =
  "<b>01 Design</b> — drag any parameter chip up/down to adjust it; tap it to fine-tune on the right slider. 🎲 Random / ≈ Mutate roll or nudge the sound. " +
  "<b>Morph</b> blends two sounds into a new one: click <b>A</b> and <b>B</b> to grab the current sound, or drop bank tiles on them. " +
  "<b>02 Bank</b> — click an empty slot to save the sound you just made. " +
  "<b>03 Boxes</b> — drop a tile on a box, or focus a box and hold <b>Record</b> to play notes into it. " +
  "<b>04 Song</b> — drag a box onto the canvas or press <b>+ Song</b>. " +
  "<b>Keyboard modes</b> (bar above the keys) — <b>Chord</b> plays a whole chord per key (pick an in-key scale and you can't play a wrong one); <b>Harm</b> stacks the note's natural overtones. <b>Space</b> plays/stops.";

// Keep fixed docks from covering content.
// Desktop stack (bottom-up): input-dock → ctx-slider.
function fitDock(): void {
  const inputDock = document.querySelector<HTMLElement>(".input-dock");
  if (!inputDock) return;
  const inputH = inputDock.getBoundingClientRect().height + 16;
  const ctx = document.querySelector<HTMLElement>(".ctx-slider");
  if (ctx) ctx.style.bottom = `${inputH}px`;
  undoChip.style.bottom = `${inputH + 8}px`;
  document.body.style.paddingBottom = `${inputH}px`;
}
requestAnimationFrame(fitDock);
window.addEventListener("resize", fitDock);

// The top bar (Record · Play · sample row) is sticky at top:0; the header
// waveform scrolls away above it. Keep the fixed right-edge ctx-slider riding
// just under the top bar.
const HEADER_H = 96;
function followPalette(): void {
  const ctx = document.querySelector<HTMLElement>(".ctx-slider");
  if (!ctx) return;
  const barH = document.getElementById("top-bar")?.offsetHeight ?? 64;
  const y = Math.min(Math.max(window.scrollY, 0), HEADER_H);
  ctx.style.top = `${HEADER_H - y + barH}px`;
}
window.addEventListener("scroll", followPalette, { passive: true });
window.addEventListener("resize", followPalette);
followPalette();
